// app/src/features/layout/useForecastSelection.js
// Hook order: `useForecastSelection` is called early (the timeline geometry reads `compareMode`
// and the boot hook takes `selectForecastRef`); `useForecastSelectionActions` runs after
// `useBootForecasts`, whose `reattachToForecast` it closes over.
import { useCallback, useEffect, useRef, useState } from "react";
import { useForecastOrder } from "../../hooks/useForecastOrder.js";
import { useForecastStacks } from "../../hooks/useForecastStacks.js";
import { boardOrderIds, moveIdInOrder, orderForecasts } from "../../lib/forecast/orderForecasts.js";
import { makeIsWatchlisted, mergeByReceivedAt } from "../../lib/forecast/forecastLists.js";
import { emitForecastReorder, emitStackArrange } from "../../lib/telemetry/uiIntents.js";
import { activeSet, comparisonSet } from "../../lib/forecast/reducer.js";
import {
  ACTIVE_FORECAST_KEY,
  sharedRerunHandleFor,
  removeDetachedRunId,
} from "../../lib/forecast/storageKeys.js";
import { shouldReattachOnSelect } from "../../lib/forecast/resume.js";
import { notePhoneOpen } from "../../hooks/useForecastOpenIntent.js";
import { clearForecastAlert } from "../../commands/deleteForecast.js";

/**
 * The board's order, stacks, compare mode, the pending delete selection and the forward ref the
 * boot restore calls selectForecast through.
 *
 * @param {object} deps
 * @param {object} deps.preferences  users.preferences (useAuthSession)
 * @param {Function} deps.setPreferences
 * @param {Array} deps.forecasts  the owner leg (selectLegacyForecasts ∪ watchlist)
 * @param {object|null} deps.active
 * @param {object|null} deps.viewSnap  the rewound snapshot (useReforecastScrub)
 * @param {string|null} deps.viewMomentIso
 * @param {{current: object}} deps.pinnedRef  the committed watchlist map (AppInner's mirror)
 */
export function useForecastSelection({
  preferences,
  setPreferences,
  forecasts,
  active,
  viewSnap,
  viewMomentIso,
  pinnedRef,
}) {
  // Manual Forecast Manager ordering, persisted per-user (users.preferences.forecastOrder).
  const {
    order: forecastOrder,
    saveOrder: saveForecastOrder,
    removeFromOrder: removeFromForecastOrder,
  } = useForecastOrder({
    preferences,
    setPreferences,
  });
  // Truncating `history` keeps the row's own computed move consistent with the overridden move. The
  // ticker `move` is the change AT the viewed snapshot (its probability vs the snapshot immediately
  // before it); the first snapshot has no prior, so no ticker.
  const logForecasts = (() => {
    if (!viewSnap || !active) return forecasts;
    const hist = active.history || [];
    const k = hist.findIndex((h) => h.generatedAt === viewMomentIso);
    const prev = k > 0 ? hist[k - 1]?.contract?.probability : null;
    const curr = viewSnap.contract?.probability;
    let move = null;
    if (typeof prev === "number" && typeof curr === "number") {
      const diff = curr - prev,
        val = Math.round(Math.abs(diff) * 100);
      if (val !== 0) move = { dir: diff > 0 ? "up" : "down", val };
    }
    const cutMs = new Date(viewMomentIso).getTime();
    // Guard falsy entries (selectLegacyForecasts can include null/undefined —
    // see the `f&&f.alert` guard elsewhere) so this render-time map never throws,
    // and don't spread a missing contract.
    return forecasts.map((f) =>
      f && f.id === active.id
        ? {
            ...f,
            contract: { ...(f.contract || {}), probability: curr ?? f.contract?.probability },
            history: hist.filter((h) => new Date(h.generatedAt).getTime() < cutMs),
            move,
          }
        : f,
    );
  })();
  // Manual per-user ordering, applied before the list reaches the Forecast Manager;
  // the watchlist/recent section filter preserves this relative order.
  const orderedLogForecasts = orderForecasts(logForecasts, forecastOrder);
  // The board lists owned AND shared rows, so a drag must order over both: read through a ref,
  // since sharedForecasts is defined further down.
  const sharedForecastsRef = useRef([]);
  // Reorders WITHIN a section only (watchlist vs recent): a cross-section drop is a no-op, since
  // dragging never pins or unpins. Captures the full CURRENT displayed order, so a never-positioned
  // item gets baked in.
  const handleReorderForecast = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- pinnedRef arrives through the hook's argument object (stable: AppInner's useRef); the manual deps are verbatim from AppInner
    (fromId, toId) => {
      if (!fromId || !toId || fromId === toId) return;
      const byId = new Map(
        mergeByReceivedAt(forecasts, sharedForecastsRef.current)
          .filter(Boolean)
          .map((f) => [f.id, f]),
      );
      // Same shared predicate the log variants use — built off pinnedRef (not the
      // render-closure `pinned`) so a drag immediately after a pin click sees the
      // committed value. Falls back to the row itself for the resolved-watchlist case.
      const isW = makeIsWatchlisted(pinnedRef.current);
      const inWatchlist = (id) => isW(byId.get(id) ?? { id });
      if (inWatchlist(fromId) !== inWatchlist(toId)) return;
      const displayed = boardOrderIds(forecasts, sharedForecastsRef.current, forecastOrder);
      const from = displayed.indexOf(fromId);
      const to = displayed.indexOf(toId);
      if (from < 0 || to < 0) return;
      const next = displayed.filter((id) => id !== fromId);
      const anchor = next.indexOf(toId);
      const insertAt = from < to ? anchor + 1 : anchor; // down → after target; up → before
      next.splice(insertAt, 0, fromId);
      emitForecastReorder(fromId);
      saveForecastOrder(next);
      // pinnedRef is a stable ref (read in the body, not a reactive dep).
    },
    [forecasts, forecastOrder, saveForecastOrder], // eslint-disable-line react-hooks/exhaustive-deps -- pinnedRef is AppInner's useRef, a hook input here; the array is verbatim from AppInner, where the rule saw the ref
  );
  // The rail layout's board drag: place a forecast (or a stack's forecasts, as a block)
  // before/after a row, in any section. A whole stack moved is `stack.arrange` (`place`) for
  // its `stackId`; one forecast moved is `forecast.reorder`.
  const moveForecastInOrder = useCallback(
    (id, beforeId, afterId, { stackId = null } = {}) => {
      if (stackId) emitStackArrange(stackId, "place");
      else emitForecastReorder(id);
      saveForecastOrder(
        moveIdInOrder(
          boardOrderIds(forecasts, sharedForecastsRef.current, forecastOrder),
          id,
          beforeId,
          afterId,
        ),
      );
    },
    [forecasts, forecastOrder, saveForecastOrder],
  );
  // Forecast stacks — named bundles of forecasts in the Forecasts panel, saved per account in
  // users.preferences.forecastStacks.
  const forecastStacking = useForecastStacks({ preferences, setPreferences, enabled: true });
  // A forecast opened from "Forecasts you may be interested in", or updated by hand (Update now),
  // goes to the top of its list, as a question just asked would; its creation time is the
  // forecast's own, often days back. Scheduled updates leave the order alone, and so does a card
  // in the Watchlist, where the order is the user's: a watched card, or any card of a stack with a
  // watched card in it (such a stack sits in the Watchlist, layoutWithStacks). A stack sits where
  // its lead card falls in the list, so a stacked card leads its stack and the whole stack goes
  // first; moving the card alone would leave the stack where it was.
  const moveForecastToTop = useCallback(
    (id) => {
      const stack = forecastStacking.stacks.find((s) => s.cardIds.includes(id));
      const rows = new Map(
        mergeByReceivedAt(forecasts, sharedForecastsRef.current)
          .filter(Boolean)
          .map((f) => [f.id, f]),
      );
      const isW = makeIsWatchlisted(pinnedRef.current);
      if ((stack?.cardIds ?? [id]).some((c) => isW(rows.get(c) ?? { id: c }))) return;
      const ids = boardOrderIds(forecasts, sharedForecastsRef.current, forecastOrder);
      if (stack) forecastStacking.onPromote(id);
      const block = [id, ...(stack?.cardIds ?? []).filter((c) => c !== id && ids.includes(c))];
      const next = [...block, ...ids.filter((x) => !block.includes(x))];
      if (next.length === ids.length && next.every((x, i) => x === ids[i])) return;
      saveForecastOrder(next);
    },
    [forecasts, forecastOrder, saveForecastOrder, forecastStacking, pinnedRef],
  );
  // Forward ref to selectForecast() (assigned by useForecastSelectionActions, right after
  // useBootForecasts — it needs that hook's reattachToForecast). bootForecasts's restore decision
  // reads it the same way, for the same reason.
  const selectForecastRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [, setDetailLevel] = useState(5); // 1-5, 5=100%
  const [compareMode, setCompareMode] = useState(false);
  // The branch reattach paths are useCallback([]) / useCallback([forecasts]) and run long after
  // mount, so they read this ref instead of capturing a stale compareMode.
  const compareModeRef = useRef(compareMode);
  useEffect(() => {
    compareModeRef.current = compareMode;
  }, [compareMode]);
  return {
    forecastOrder,
    saveForecastOrder,
    removeFromForecastOrder,
    logForecasts,
    orderedLogForecasts,
    sharedForecastsRef,
    handleReorderForecast,
    moveForecastInOrder,
    forecastStacking,
    moveForecastToTop,
    selectForecastRef,
    deleteConfirm,
    setDeleteConfirm,
    setDetailLevel,
    compareMode,
    setCompareMode,
    compareModeRef,
  };
}

/**
 * Selecting a forecast: the one owner of "which forecast is active" and its phone counterpart.
 * Called after `useBootForecasts`; assigns `selectForecastRef` (the boot restore reads it) and,
 * on a phone, `handleSelectRef`.
 *
 * @param {object} deps
 * @param {Function} deps.dispatchRun  the store's dispatch, handed in (a feature imports no store, C-23)
 * @param {Function} deps.reattachToForecast  useBootForecasts's reattach entry point
 * @param {{current: Function|null}} deps.selectForecastRef
 * @param {{current: Function|null}} deps.handleSelectRef  AppInner's forward ref to the Forecast
 *   Manager's row handler (the deep link, the chat journal and the featured list call through it)
 * @param {boolean} deps.isPhone
 * @param {boolean} deps.forecastTracking
 * @param {() => object} deps.mkDeleteDeps  the delete command's deps (AppInner's)
 */
export function useForecastSelectionActions({
  dispatchRun,
  reattachToForecast,
  selectForecastRef,
  handleSelectRef,
  isPhone,
  forecastTracking,
  mkDeleteDeps,
}) {
  // Single owner of "which forecast is active". Persists the active id (running rows included
  // — the restore decision on reload picks reattach or load), and — when asked — reattaches a
  // still-running row's stream via the one reattach entry point (idempotent:
  // reattachToForecast claims the trace and no-ops if this tab already streams it).
  const selectForecast = useCallback(
    (forecast, { reattach = false, seedChat = true } = {}) => {
      dispatchRun(activeSet(forecast?.id ?? null));
      // Re-opening a forecast clears any CLEAR-detach mark on it: the user is
      // viewing it again, so a subsequent reload should treat it as active.
      if (forecast?.id) removeDetachedRunId(forecast.id);
      try {
        if (forecast && forecast.id && (!forecast._pending || forecast.traceId)) {
          sessionStorage.setItem(ACTIVE_FORECAST_KEY, forecast.id);
        } else if (!forecast) {
          sessionStorage.removeItem(ACTIVE_FORECAST_KEY);
        }
      } catch (_e) {
        /* private mode / quota — skip */
      }
      // shouldReattachOnSelect withholds the foreground reattach for a shared row (isOwn:false):
      // its live updates already arrive over the generic realtime hints channel, so a second,
      // dedicated stream-reattach here would also wrongly claim the global
      // active/forecastingPhase/chat as if this tab owned the row's generation. Calm gate, not a
      // security boundary — the server's shared read predicate already governs what a shared
      // viewer may fetch (lifecycle.js's resume gate).
      if (reattach && shouldReattachOnSelect(forecast, sharedRerunHandleFor(forecast?.id))) {
        // Carry the row's contract so the resume keeps showing the preprocessed
        // contract (resolution date / criteria) during the run instead of
        // reverting the panel to bare question text. The row's contract is the
        // submit-time earlyContract (in-tab) or the server-persisted preprocessed
        // contract (after a reload-then-click). reattachToForecast merges it over
        // its bare placeholder.
        reattachToForecast(
          {
            traceId: forecast.traceId,
            forecastId: forecast.forecastId,
            clientId: forecast.id,
            question: forecast.question || "",
            contract: forecast.contract || null,
          },
          null,
          { seedChat },
        );
      }
    },
    [reattachToForecast], // eslint-disable-line react-hooks/exhaustive-deps -- dispatchRun is the store's module-level dispatch, a hook input here (C-23); the array is verbatim from AppInner, where it was an import
  );
  // Assigned during render so useBootForecasts's restore decision invokes the current
  // selectForecast.
  // eslint-disable-next-line react-hooks/refs -- intentional render-time forward-ref assignment (verbatim from App.jsx): the boot restore decision calls through it after mount
  selectForecastRef.current = selectForecast;
  // Opening a forecast on a phone just focuses it (no timeline to frame). It also
  // serves the alert email's ?forecast= deep link, which the desktop routes through the list.
  const openOnPhone = (f) => {
    notePhoneOpen();
    dispatchRun(comparisonSet([f.id]));
    selectForecast(f);
    if (forecastTracking && f.alert) clearForecastAlert(mkDeleteDeps(), { runId: f.id });
  };
  // eslint-disable-next-line react-hooks/refs -- intentional render-time forward-ref assignment (verbatim from App.jsx): the phone layout has no Forecast Manager to assign it
  if (isPhone) handleSelectRef.current = openOnPhone;
  return { selectForecast, openOnPhone };
}
